<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useUserStore } from './stores/user'
import { useAuth } from './composables/useAuth'
import ToastContainer from './components/ToastContainer.vue'
import { useBranding } from './composables/useBranding'
import { routeAnnouncement } from './utils/pageTitle'
const userStore = useUserStore()
const { getAccessTokenSilently } = useAuth()
// Apply advisor white-label accent app-wide (no-op unless branding is enabled)
useBranding()

// When the user returns to the tab, quietly re-pull their profile so an
// admin-granted change mid-session — enabling Sweep is the motivating case —
// shows up without a logout/login (and a revoke hides it, symmetrically). The
// backend already computes features fresh on /auth/me; the SPA just never
// re-read them within a session. Throttled so rapid tab switches don't spam.
let lastProfileRefresh = 0
async function refreshOnFocus() {
    if (document.visibilityState !== 'visible') return
    if (!userStore.user || userStore.isImpersonating) return
    if (Date.now() - lastProfileRefresh < 15000) return
    lastProfileRefresh = Date.now()
    try {
        const token = await getAccessTokenSilently()
        if (token) await userStore.refreshProfile(token)
    } catch {
        // No valid token (e.g. dev-login) or a transient error — leave the profile as-is.
    }
}
onMounted(() => {
    document.addEventListener('visibilitychange', refreshOnFocus)
    window.addEventListener('focus', refreshOnFocus)
})
onUnmounted(() => {
    document.removeEventListener('visibilitychange', refreshOnFocus)
    window.removeEventListener('focus', refreshOnFocus)
})
</script>

<template>
  <!-- One viewport-high column: the banner takes its own row so pages never
       overflow the window (AppLayout scrolls inside its own <main>) -->
  <div class="h-screen print:h-auto flex flex-col">
  <div v-if="userStore.isImpersonating" class="shrink-0 bg-cc-warning/10 border-b border-cc-warning/20 px-4 py-2 flex items-center justify-between">
    <div class="flex items-center gap-2">
      <span class="text-cc-warning font-medium flex items-center gap-1.5"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg> Impersonating: {{ userStore.user?.name || 'Unknown User' }}</span>
      <span class="text-cc-text text-sm">({{ userStore.user?.role }})</span>
    </div>
    <button
      @click="userStore.stopImpersonation()"
      class="px-3 py-1 bg-cc-warning text-cc-btn-text text-sm font-medium rounded hover:opacity-90 transition"
    >
      Exit Impersonation
    </button>
  </div>
  <div class="flex-1 min-h-0 overflow-y-auto print:overflow-visible">
    <router-view />
  </div>
  </div>
  <ToastContainer />
  <!-- Announces the new page title to screen readers after client-side navigation -->
  <div aria-live="polite" aria-atomic="true" class="sr-only">{{ routeAnnouncement }}</div>
  <!-- Terms acceptance lives on its own route (/terms); the router guard
       sends any user without the active version there. -->
</template>
